// BlogSection.tsx
import React, { useEffect, useState } from 'react';
import { fadeTimeout, getOverallPadding } from '../helpers/Themes';
import { Button, Collapse, Divider, Fade, Typography } from '@mui/material';
import { useCustomTheme } from '../contexts/ThemeContext';
import { useApi } from '../contexts/ApiContext';
import {
    Blog,
    User,
    UserAccountTypes,
    UserProfile,
} from '../workers/ApiWorker';
import { createWorkerFactory, useWorker } from '@shopify/react-web-worker';
import Box from '@mui/material/Box';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import NoResultsFound from '../components/universal/loader/NoResultsFound';
import BackgroundHero from '../components/backgrounds/BackgroundHero';
import Tag from '../components/universal/chips/Tag';
import { PeopleAlt } from '@mui/icons-material';
import { CalendarIcon } from '@mui/x-date-pickers';
import { convertSimple } from '../helpers/Date';
import BlogSectionArea from '../components/blog/BlogSectionArea';
import { Form, Formik } from 'formik';
import TextInput from '../components/universal/inputs/TextInput';
import RichTextInput from '../components/universal/inputs/RichTextInput';
import * as Yup from 'yup';
import Breadcrumb from '../components/universal/breadcrumbs/breadcrumb';

interface ViewBlogPageProps {
    isMobile: boolean;
    padding?: number;
    user?: User;
}

const createApiWorker = createWorkerFactory(
    () => import('../workers/ApiWorker')
);

const ViewBlogPage: React.FC<ViewBlogPageProps> = ({
    isMobile,
    padding,
    user,
}) => {
    const location = useLocation();
    const { theme } = useCustomTheme();
    const { token } = useApi();
    const navigate = useNavigate(); // Hook for navigation
    const apiWorker = useWorker(createApiWorker);
    const { slug } = useParams<{ slug: string }>(); // Extract uuid from URL

    const [blog, setBlog] = React.useState<Blog | null>(null);
    const [loadingResults, setLoadingResults] = useState<boolean>(true);
    const [noResultsFound, setNoResultsFound] = useState<boolean>(false);
    const [openEditor, setOpenEditor] = useState(false);

    // Validation Schema
    const validationSchema = Yup.object({
        name: Yup.string().trim().required('Section name is required'),
        content: Yup.string().required('Content is required'),
    });

    const handlePublishBlog = async () => {
        if (!blog || !token) return;

        try {
            // ✅ API call
            const publishBlog = await apiWorker.publishBlog(token, blog.uuid);

            setBlog(publishBlog.data);
        } catch (err) {
            // TODO
        }
    }

    const handleDeleteSection = async (index: number, sectionUuid: string) => {
        if (!blog || !token) return;

        try {
            // ✅ API call
            await apiWorker.deleteBlogSection(token, blog.uuid, sectionUuid);

            // ✅ On success: remove from UI
            setBlog((prev) => {
                if (!prev) return prev;
                const nextSections =
                    prev.sections?.filter((_, i) => i !== index) ?? [];
                return { ...prev, sections: nextSections };
            });
        } catch (err) {
            // TODO
        }
    };

    const handleTagUserClick = (userProfile: UserProfile) => {
        navigate(`/${userProfile.username}`, {
            state: { userProfileUuid: userProfile.uuid, fresh: true },
        });
    };

    useEffect(() => {
        const getBlog = async () => {
            if (slug) {
                setLoadingResults(true);
                try {
                    const response = await apiWorker.getBlog(slug, token);

                    setLoadingResults(false);
                    setBlog(response.data);
                } catch (error) {
                    setNoResultsFound(true);
                }
            }
        };
        getBlog()
            .then(() => {})
            .catch((error) => {
                console.log('Error fetching blog:', error);
            });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location]);

    return (
        <Fade in={true} timeout={fadeTimeout}>
            <div>
                <Box sx={{ position: 'relative', overflow: 'visible' }}>
                    {noResultsFound && !blog ? (
                        <NoResultsFound />
                    ) : (
                        <>
                            {blog && (
                                <Fade in={true} timeout={fadeTimeout}>
                                    <div>
                                        <BackgroundHero
                                            padding={padding}
                                            image={blog?.images?.main}
                                            isMobile={isMobile}
                                            fadeOnScroll={true}
                                        >
                                            <Box
                                                sx={{
                                                    position: 'relative',
                                                    zIndex: 1,
                                                    width: '100%',
                                                    maxWidth: 1200,
                                                    marginTop: isMobile
                                                        ? 0
                                                        : 20,
                                                    paddingLeft: blog.images
                                                        ?.main
                                                        ? 0
                                                        : getOverallPadding(
                                                              isMobile,
                                                              padding
                                                          ),
                                                    paddingRight: blog.images
                                                        ?.main
                                                        ? 0
                                                        : getOverallPadding(
                                                              isMobile,
                                                              padding
                                                          ),
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        flexWrap: 'wrap', // still allows wrapping
                                                        gap: 1,
                                                        overflow: 'auto',
                                                        paddingLeft:
                                                            getOverallPadding(
                                                                isMobile,
                                                                padding
                                                            ),
                                                        paddingRight:
                                                            getOverallPadding(
                                                                isMobile,
                                                                padding
                                                            ),
                                                    }}
                                                >
                                                    <Breadcrumb
                                                        section={{
                                                            label: 'Blogs',
                                                            path: '/blog',
                                                        }}
                                                        items={[
                                                            {
                                                                label: blog.name,
                                                            },
                                                        ]}
                                                    />
                                                </Box>
                                                <Typography
                                                    variant={
                                                        isMobile ? 'h3' : 'h1'
                                                    }
                                                    sx={{
                                                        marginBottom: 1,
                                                        cursor: 'default',
                                                        color: blog.images?.main
                                                            ? '#fff'
                                                            : theme.palette.text
                                                                  .secondary,
                                                        fontWeight: 700,
                                                    }}
                                                >
                                                    {blog.name}
                                                </Typography>

                                                <Typography
                                                    variant="h6"
                                                    sx={{
                                                        marginBottom: isMobile
                                                            ? 1
                                                            : 2,
                                                        cursor: 'default',
                                                        color: blog.images?.main
                                                            ? '#e0e0e0'
                                                            : theme.palette.text
                                                                  .disabled,
                                                        maxWidth: isMobile
                                                            ? '100%'
                                                            : '60%',
                                                        textAlign: 'center',
                                                        marginLeft: 'auto',
                                                        marginRight: 'auto',
                                                    }}
                                                >
                                                    {blog.miniDescription}
                                                </Typography>

                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        flexWrap: 'wrap', // still allows wrapping
                                                        gap: 1,
                                                        overflow: 'auto',
                                                        justifyContent:
                                                            'center', // ✅ horizontally center
                                                        alignItems: 'center', // ✅ vertically center (optional)
                                                        textAlign: 'center',
                                                    }}
                                                >
                                                    <Tag
                                                        blog={true}
                                                        text={
                                                            blog.live
                                                                ? convertSimple(
                                                                      blog.live
                                                                  )
                                                                : 'Draft'
                                                        }
                                                        removable={false}
                                                        icon={<CalendarIcon />}
                                                    />

                                                    <Tag
                                                        searchable={true}
                                                        onClick={() => {
                                                            handleTagUserClick(
                                                                blog.author
                                                            );
                                                        }}
                                                        blog={true}
                                                        text={
                                                            blog.author.username
                                                        }
                                                        removable={false}
                                                        icon={<PeopleAlt />}
                                                    />
                                                </Box>
                                                {blog.sections &&
                                                    blog.sections.length >
                                                        0 && (
                                                        <Box
                                                            sx={{
                                                                marginTop: 6,
                                                                display: 'flex',
                                                                flexDirection:
                                                                    'column',
                                                                gap: 0,
                                                            }}
                                                        >
                                                            {blog.sections?.map(
                                                                (
                                                                    section,
                                                                    index
                                                                ) => (
                                                                    <BlogSectionArea
                                                                        user={
                                                                            user
                                                                        }
                                                                        blogUuid={
                                                                            blog.uuid
                                                                        }
                                                                        key={
                                                                            section.uuid
                                                                        } // keep stable React key
                                                                        section={
                                                                            section
                                                                        }
                                                                        live={
                                                                            blog.live
                                                                        }
                                                                        isMobile={
                                                                            isMobile
                                                                        }
                                                                        index={
                                                                            index
                                                                        } // ← pass array order
                                                                        onDeleteSection={
                                                                            handleDeleteSection
                                                                        } // ← pass handler
                                                                        totalSections={
                                                                            blog
                                                                                .sections
                                                                                ?.length ??
                                                                            0
                                                                        }
                                                                    />
                                                                )
                                                            )}
                                                        </Box>
                                                    )}

                                                {user &&
                                                    user.userProfile
                                                        .accountType !==
                                                        UserAccountTypes.MEMBER && (
                                                        <Box
                                                            sx={{
                                                                marginTop: 0,
                                                                display: 'flex',
                                                                flexDirection:
                                                                    'column',
                                                                gap: 0,
                                                            }}
                                                        >
                                                            <Divider />
                                                            <Box
                                                                sx={{
                                                                    width: '100%',
                                                                    maxWidth: 900,
                                                                    margin: '0 auto',
                                                                    marginBottom: 10,
                                                                    textAlign:
                                                                        'left',
                                                                    mt: 4,
                                                                    px: 2,
                                                                    '& img': {
                                                                        maxWidth:
                                                                            '100%',
                                                                        height: 'auto',
                                                                        borderRadius: 2,
                                                                        display:
                                                                            'block',
                                                                        margin: '12px 0',
                                                                    },
                                                                }}
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        display:
                                                                            'flex',
                                                                        gap: 1,
                                                                    }}
                                                                >
                                                                    <Button
                                                                        variant={
                                                                            'outlined'
                                                                        }
                                                                        color={
                                                                            openEditor
                                                                                ? 'inherit'
                                                                                : 'warning'
                                                                        }
                                                                        onClick={() =>
                                                                            setOpenEditor(
                                                                                (
                                                                                    v
                                                                                ) =>
                                                                                    !v
                                                                            )
                                                                        }
                                                                    >
                                                                        {openEditor
                                                                            ? 'Cancel'
                                                                            : 'Add Section'}
                                                                    </Button>
                                                                    {!blog.live && (
                                                                        <Button
                                                                            variant="contained"
                                                                            color="primary"
                                                                            onClick={
                                                                                handlePublishBlog
                                                                            }
                                                                        >
                                                                            Publish
                                                                            Blog
                                                                        </Button>
                                                                    )}
                                                                </Box>

                                                                <Collapse
                                                                    in={
                                                                        openEditor
                                                                    }
                                                                    unmountOnExit
                                                                >
                                                                    <Formik
                                                                        enableReinitialize
                                                                        initialValues={{
                                                                            name: '',
                                                                            content:
                                                                                '',
                                                                        }}
                                                                        validationSchema={
                                                                            validationSchema
                                                                        }
                                                                        onSubmit={async (
                                                                            values,
                                                                            {
                                                                                resetForm,
                                                                            }
                                                                        ) => {
                                                                            if (
                                                                                !token ||
                                                                                !blog
                                                                            ) {
                                                                                return;
                                                                            }

                                                                            try {
                                                                                const newSection =
                                                                                    await apiWorker.postBlogSection(
                                                                                        values,
                                                                                        token,
                                                                                        blog.uuid
                                                                                    );

                                                                                setOpenEditor(
                                                                                    false
                                                                                );
                                                                                setBlog(
                                                                                    newSection.data
                                                                                );
                                                                            } catch (error) {
                                                                                // Handle error
                                                                            }
                                                                        }}
                                                                    >
                                                                        {({
                                                                            errors,
                                                                            touched,
                                                                            values,
                                                                            handleChange,
                                                                            isSubmitting,
                                                                            handleBlur,
                                                                            isValid,
                                                                            setFieldValue,
                                                                        }) => (
                                                                            <Form>
                                                                                <TextInput
                                                                                    id="name"
                                                                                    label="SECTION NAME *"
                                                                                    value={
                                                                                        values.name
                                                                                    }
                                                                                    handleChange={
                                                                                        handleChange
                                                                                    }
                                                                                    handleBlur={
                                                                                        handleBlur
                                                                                    }
                                                                                    hasSubmitted={
                                                                                        isSubmitting
                                                                                    }
                                                                                    touched={
                                                                                        touched.name
                                                                                    }
                                                                                    errors={
                                                                                        errors.name
                                                                                    }
                                                                                    isMobile={
                                                                                        isMobile
                                                                                    }
                                                                                />

                                                                                <RichTextInput
                                                                                    images={
                                                                                        true
                                                                                    }
                                                                                    id="content"
                                                                                    label="CONTENT *"
                                                                                    value={
                                                                                        values.content
                                                                                    }
                                                                                    errors={
                                                                                        errors.content
                                                                                    }
                                                                                    handleBlur={
                                                                                        handleBlur
                                                                                    }
                                                                                    hasSubmitted={
                                                                                        isSubmitting
                                                                                    }
                                                                                    touchedFormik={
                                                                                        touched.content
                                                                                    }
                                                                                    handleChange={
                                                                                        setFieldValue
                                                                                    }
                                                                                    isMobile={
                                                                                        isMobile
                                                                                    }
                                                                                />

                                                                                <Button
                                                                                    sx={{
                                                                                        marginTop: 1,
                                                                                    }}
                                                                                    type="submit"
                                                                                    disabled={
                                                                                        isSubmitting ||
                                                                                        !isValid
                                                                                    }
                                                                                    variant="contained"
                                                                                >
                                                                                    SAVE
                                                                                    SECTION
                                                                                </Button>
                                                                            </Form>
                                                                        )}
                                                                    </Formik>
                                                                </Collapse>
                                                            </Box>
                                                        </Box>
                                                    )}
                                            </Box>
                                        </BackgroundHero>
                                    </div>
                                </Fade>
                            )}
                        </>
                    )}
                </Box>
            </div>
        </Fade>
    );
};

export default ViewBlogPage;
